import { ref, watch, onMounted } from 'vue';
import { useInternational } from '@/common/locale';
import { ColumnVisibilityInfo } from '@/common/components/molecules/modernGrid/modernGrid.types';

export interface Props {
  columnSettingList: ColumnVisibilityInfo[];
}

export interface Emit {
  (e: 'change-column-visibility', value: ColumnVisibilityInfo[]): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const tooltipRef = ref();
  const columnSettings = ref<ColumnVisibilityInfo[]>([]);
  const checkColumnGroup = ref<string[]>([]);
  const searchWord = ref('');
  let lastItem = '';

  const onCheckColumn = (checkedValues: string[]) => {
    if (checkedValues.length === 1) {
      [lastItem] = checkedValues;
    }
    if (checkedValues.length === 0) {
      checkColumnGroup.value = [lastItem];
    }
  };

  const initCheckedList = (settingList: ColumnVisibilityInfo[]) => {
    checkColumnGroup.value = settingList
      ?.filter(({ visibility }) => !!visibility)
      ?.map(({ text }) => text);
  };

  const onSave = () => {
    const updatedList = props.columnSettingList?.map((column) => ({
      ...column,
      visibility: checkColumnGroup.value.includes(column.text),
    }));

    emit('change-column-visibility', updatedList);
    searchWord.value = '';
    tooltipRef.value?.hide();
  };

  watch(searchWord, (newValue) => {
    const searchValue = newValue.toLowerCase().trim();

    if (!searchValue) {
      columnSettings.value = props.columnSettingList;
      return;
    }

    columnSettings.value = props.columnSettingList?.filter(({ label }) => {
      const labelValue = (label || '').toLowerCase();
      return labelValue.includes(searchValue);
    });
  });

  watch(
    () => props.columnSettingList,
    (settingList) => {
      columnSettings.value = settingList;
      initCheckedList(settingList);
      if (checkColumnGroup.value.length === 1) {
        [lastItem] = checkColumnGroup.value;
      }
    },
    { immediate: true, deep: true },
  );

  onMounted(() => {
    watch(
      () => tooltipRef.value?.isShowTooltip,
      (value) => {
        if (value) {
          initCheckedList(props.columnSettingList);
        }
      },
    );
  });

  return { tooltipRef, t, columnSettings, checkColumnGroup, searchWord, onCheckColumn, onSave };
};
